import { DEFAULT_SERVICE } from '@xeetr/types/defaults'
import { ViewService } from '@xeetr/types/models'
import { copyService } from '@xeetr/types/utils'
import { useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import AddObjectButton from '../../components/settings/common/AddObjectButton'
import ServiceForm from '../../components/settings/services/ServiceForm'
import ServiceHeader from '../../components/settings/services/ServiceHeader'
import ServicesFooter from '../../components/settings/services/ServicesFooter'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import Loading from '@/components/loading/Loading'
import { useConfirmDialog } from '@/hooks/common/useConfirmDialog'
import { useServices } from '@/hooks/useServices'
import { useServicesMutation } from '@/hooks/useServicesMutation'
import { DirtyFormsMap, FormControlHandle } from '@/types/ServiceForm'

const Services = () => {
  const { services, isLoading } = useServices()
  const { createService, updateService, deleteService } = useServicesMutation()
  const [expandedItems, setExpandedItems] = useState<string[]>([])
  const [activeFormControl, setActiveFormControl] = useState<FormControlHandle | null>(null)
  const [dirtyForms, setDirtyForms] = useState<DirtyFormsMap>(new Map())
  const confirmDialog = useConfirmDialog()

  const handleCreate = () => {
    const newService: ViewService = {
      ...DEFAULT_SERVICE,
      id: uuidv4(),
    }
    createService(newService)
    // Expand the new service after creation
    setTimeout(() => setExpandedItems([newService.id]), 100)
  }

  const handleCopy = (service: ViewService) => {
    const copiedService: ViewService = {
      ...copyService(service),
      id: uuidv4(),
    }
    createService(copiedService)
  }

  const handleDelete = (id: string) => {
    confirmDialog.confirm(
      {
        title: 'Löschen bestätigen!',
        description: 'Möchten Sie die Dienstleistung wirklich löschen?',
        confirmText: 'Löschen',
        cancelText: 'Abbrechen',
        variant: 'destructive',
      },
      () => {
        deleteService(id)
        // Remove from expanded if was open
        setExpandedItems((prev) => prev.filter((item) => item !== id))
      }
    )
  }

  const handleSwap = (fromIndex: number, toIndex: number) => {
    // Update order for both services
    const servicesCopy = [...services]
    const fromService = servicesCopy[fromIndex]
    const toService = servicesCopy[toIndex]

    updateService({
      id: fromService.id,
      service: { order: toService.order },
    })
    updateService({
      id: toService.id,
      service: { order: fromService.order },
    })
  }

  /**
   * Handle accordion value change with dirty form warning
   * If user tries to close a dirty form, show confirmation dialog
   */
  const handleAccordionChange = (newExpandedItems: string[]) => {
    const closingItems = expandedItems.filter((item) => !newExpandedItems.includes(item))

    // Check if any closing item has dirty form
    const dirtyClosingItem = closingItems.find((item) => dirtyForms.get(item))

    if (dirtyClosingItem) {
      // Show warning dialog
      confirmDialog.confirm(
        {
          title: 'Nicht gespeicherte Änderungen',
          description:
            'Sie haben nicht gespeicherte Änderungen. Möchten Sie diese verwerfen und fortfahren?',
          confirmText: 'Verwerfen',
          cancelText: 'Abbrechen',
          variant: 'destructive',
        },
        () => {
          // User confirmed, allow close and discard changes
          setExpandedItems(newExpandedItems)
          setDirtyForms((prev) => {
            const next = new Map(prev)
            next.delete(dirtyClosingItem)
            return next
          })
        }
      )
    } else {
      // No dirty forms, allow close
      setExpandedItems(newExpandedItems)
    }
  }

  /**
   * Register/unregister form control from child ServiceForm
   * Updates dirty state tracking
   */
  const handleRegisterForm = (handle: FormControlHandle | null) => {
    setActiveFormControl(handle)

    // Update dirty state map
    if (handle) {
      setDirtyForms((prev) => {
        const next = new Map(prev)
        next.set(handle.serviceId, handle.isDirty)
        return next
      })
    }
  }

  return (
    <div className="flex flex-col">
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Dienstleistungen</h2>
        <p className="text-muted-foreground text-sm">
          Verwalten Sie die Dienstleistungen Ihres Unternehmens, um Ihre Angebote zu organisieren.
        </p>
      </div>
      <div className="flex justify-end">
        <AddObjectButton append={handleCreate} label="Neue Dienstleistung" />
      </div>
      <Loading loading={isLoading} label="Dienstleistungen werden geladen...">
        <Accordion
          type="multiple"
          className="mt-4"
          value={expandedItems}
          onValueChange={handleAccordionChange}
        >
          {services.map((service, serviceIndex) => (
            <AccordionItem key={service.id} value={service.id}>
              <AccordionTrigger>
                <ServiceHeader
                  service={service}
                  serviceIndex={serviceIndex}
                  services={services}
                  swapService={handleSwap}
                />
              </AccordionTrigger>
              <AccordionContent>
                <ServiceForm
                  service={service}
                  isExpanded={expandedItems.includes(service.id)}
                  onRegisterForm={handleRegisterForm}
                  onCopy={() => handleCopy(service)}
                  onDelete={() => handleDelete(service.id)}
                  onUpdate={(updatedService) =>
                    updateService({ id: service.id, service: updatedService })
                  }
                />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Loading>
      <ConfirmDialog
        open={confirmDialog.isOpen}
        onOpenChange={confirmDialog.setIsOpen}
        title={confirmDialog.options.title}
        description={confirmDialog.options.description}
        confirmText={confirmDialog.options.confirmText}
        cancelText={confirmDialog.options.cancelText}
        variant={confirmDialog.options.variant}
        onConfirm={confirmDialog.handleConfirm}
      />
      <ServicesFooter formControl={activeFormControl} />
    </div>
  )
}

export default Services
